import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Col, Row } from 'react-bootstrap';
import VideoPlayer from './VideoPlayer';
import { BandcampAlbum, SpotifyEmbed, Video } from './content';
import { FieldSpec, useSite } from './content/context';
import { AddTile, ItemControls } from './edit/primitives';
import { compact, moveItem, removeItem, replaceItem } from './edit/list';
import { safeHttpUrl, toBandcampEmbedUrl, toSpotifyEmbedUrl, toYouTubeEmbedUrl } from './urls';

const videoFields: FieldSpec[] = [
  {
    name: 'url',
    label: 'YouTube link',
    type: 'url',
    required: true,
    placeholder: 'https://www.youtube.com/watch?v=…',
    hint: 'Copy the link from YouTube (Share → Copy). Any YouTube link works.',
    validate: (v) => (toYouTubeEmbedUrl(v) ? undefined : "That doesn't look like a YouTube video link."),
  },
  { name: 'title', label: 'Title', hint: 'Read aloud by screen readers. Not shown on the page.' },
];

const bandcampFields: FieldSpec[] = [
  {
    name: 'embedUrl',
    label: 'Bandcamp player link',
    type: 'url',
    required: true,
    placeholder: 'https://bandcamp.com/EmbeddedPlayer/album=…',
    hint: 'On Bandcamp: Share / Embed → Embed this album → pick a style, then copy just the address inside src="…".',
    validate: (v) =>
      toBandcampEmbedUrl(v) ? undefined : 'This needs to be the player address starting with https://bandcamp.com/EmbeddedPlayer/',
  },
  { name: 'title', label: 'Album title' },
  { name: 'albumUrl', label: 'Album page link', type: 'url', validate: (v) => (!v || safeHttpUrl(v) ? undefined : 'Enter a full link starting with https://') },
];

const spotifyFields: FieldSpec[] = [
  {
    name: 'url',
    label: 'Spotify link',
    type: 'url',
    required: true,
    placeholder: 'https://open.spotify.com/album/…',
    hint: 'In Spotify: … → Share → Copy link. Works for a song, album, playlist or the artist page.',
    validate: (v) => (toSpotifyEmbedUrl(v) ? undefined : "That doesn't look like a Spotify song, album, playlist or artist link."),
  },
  { name: 'title', label: 'Title', hint: 'Read aloud by screen readers. Not shown on the page.' },
];

function MusicSamples() {
  const { data, edit } = useSite();
  const { music } = data;
  const videos = music.videos ?? [];
  const albums = music.bandcamp ?? [];
  const spotify = music.spotify ?? [];

  const setVideos = (next: Video[]) => edit?.update('music', { ...music, videos: next });
  const setAlbums = (next: BandcampAlbum[]) => edit?.update('music', { ...music, bandcamp: next });
  const setSpotify = (next: SpotifyEmbed[]) => edit?.update('music', { ...music, spotify: next });

  const editSpotify = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : spotify[index];
    const result = await edit.openForm({
      title: current ? 'Edit Spotify player' : 'Add a Spotify player',
      fields: spotifyFields,
      values: { url: current?.url, title: current?.title },
      submitLabel: current ? 'Save' : 'Add player',
    });
    if (!result || !('values' in result)) return;
    const player = compact<SpotifyEmbed>(result.values);
    setSpotify(index === undefined ? [...spotify, player] : replaceItem(spotify, index, player));
  };

  const editVideo = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : videos[index];
    const result = await edit.openForm({
      title: current ? 'Edit video' : 'Add a YouTube video',
      fields: videoFields,
      values: { url: current?.url, title: current?.title },
      submitLabel: current ? 'Save' : 'Add video',
    });
    if (!result || !('values' in result)) return;
    const video = compact<Video>(result.values);
    setVideos(index === undefined ? [...videos, video] : replaceItem(videos, index, video));
  };

  const editAlbum = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : albums[index];
    const result = await edit.openForm({
      title: current ? 'Edit Bandcamp player' : 'Add a Bandcamp player',
      fields: bandcampFields,
      values: { embedUrl: current?.embedUrl, title: current?.title, albumUrl: current?.albumUrl },
      submitLabel: current ? 'Save' : 'Add player',
    });
    if (!result || !('values' in result)) return;
    const album = compact<BandcampAlbum>(result.values);
    setAlbums(index === undefined ? [...albums, album] : replaceItem(albums, index, album));
  };

  return (
    <Row className="mb-3 af-music justify-content-center">
      {edit && videos.length > 1 ? (
        <Col xs={12}>
          <p className="af-newsletter-note mb-2">The first video is shown large. Use the arrows to choose which one.</p>
        </Col>
      ) : null}
      {videos.map((video, index) => {
        const embed = toYouTubeEmbedUrl(video.url);
        if (!embed && !edit) return null;
        // The first video is featured large; the rest sit three across.
        const featured = index === 0;
        return (
          <Col xs={12} md={featured ? 12 : 6} lg={featured ? 12 : 4} className={`mb-4${featured ? " af-featured-video" : ""}`} key={`video-${index}`}>
            <div className="af-embed">
              {embed ? (
                <VideoPlayer url={embed} title={video.title || 'Arc Flash video'} />
              ) : (
                <div className="af-placeholder">This link isn't a YouTube video. Use the pencil to fix it.</div>
              )}
              <ItemControls
                noun="video"
                index={index}
                count={videos.length}
                onEdit={() => editVideo(index)}
                onMove={(from, to) => setVideos(moveItem(videos, from, to))}
                onRemove={() => setVideos(removeItem(videos, index))}
              />
            </div>
          </Col>
        );
      })}
      {albums.map((album, index) => {
        const embed = toBandcampEmbedUrl(album.embedUrl);
        if (!embed && !edit) return null;
        const albumUrl = safeHttpUrl(album.albumUrl);
        return (
          <Col md={6} className="mb-2" key={`bandcamp-${index}`}>
            <div className="af-embed">
              {embed ? (
                <iframe
                  width="100%"
                  height="315"
                  src={embed}
                  title={album.title || 'Bandcamp'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                >
                  {albumUrl ? <a href={albumUrl}>{album.title || 'Listen on Bandcamp'}</a> : null}
                </iframe>
              ) : (
                <div className="af-placeholder">This Bandcamp link won't play. Use the pencil to fix it.</div>
              )}
              <ItemControls
                noun="Bandcamp player"
                index={index}
                count={albums.length}
                onEdit={() => editAlbum(index)}
                onMove={(from, to) => setAlbums(moveItem(albums, from, to))}
                onRemove={() => setAlbums(removeItem(albums, index))}
              />
            </div>
          </Col>
        );
      })}
      {spotify.map((player, index) => {
        const embed = toSpotifyEmbedUrl(player.url);
        if (!embed && !edit) return null;
        return (
          <Col md={6} className="mb-2" key={`spotify-${index}`}>
            <div className="af-embed">
              {embed ? (
                <iframe
                  className="af-spotify"
                  width="100%"
                  height={embed.type === 'track' || embed.type === 'episode' ? 152 : 352}
                  src={embed.url}
                  title={player.title || 'Spotify player'}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              ) : (
                <div className="af-placeholder">This Spotify link won't play. Use the pencil to fix it.</div>
              )}
              <ItemControls
                noun="Spotify player"
                index={index}
                count={spotify.length}
                onEdit={() => editSpotify(index)}
                onMove={(from, to) => setSpotify(moveItem(spotify, from, to))}
                onRemove={() => setSpotify(removeItem(spotify, index))}
              />
            </div>
          </Col>
        );
      })}
      {edit ? (
        <>
          <Col md={4} lg={2} className="mb-2">
            <AddTile label="Add a YouTube video" onClick={() => editVideo()} />
          </Col>
          <Col md={4} lg={2} className="mb-2">
            <AddTile label="Add a Bandcamp player" onClick={() => editAlbum()} />
          </Col>
          <Col md={4} lg={2} className="mb-2">
            <AddTile label="Add a Spotify player" onClick={() => editSpotify()} />
          </Col>
        </>
      ) : null}
    </Row>
  );
}

export default MusicSamples;
